import React from 'react';
import { Link } from 'react-router-dom';
import { theme } from 'antd';
import { RightOutlined, FileAddOutlined, DollarOutlined, ToolOutlined } from '@ant-design/icons';
import { IconBadge } from 'components/headings';
import paths from 'assets/routes/paths';

const { useToken } = theme;

const plural = (n, sing, plur) => (n === 1 ? sing : plur);

// Columnas ACCIONABLES del ciclo (donde el ejecutivo actúa). EN_ODT y RESUELTO
// son informativas y no entran en la bandeja del Home. Cada acción deep-linkea a
// la pantalla del paso con el filtro ya aplicado.
const ACCIONES = [
  {
    etapa: 'DETECTADO',
    icon: FileAddOutlined,
    tintKey: 'colorWarning',
    label: (n) => `${n} ${plural(n, 'hallazgo listo', 'hallazgos listos')} para presupuestar`,
    to: `${paths.admin.hallazgos}?etapa=DETECTADO`,
    cta: 'Presupuestar',
  },
  {
    etapa: 'PRESUPUESTADO',
    icon: DollarOutlined,
    tintKey: 'colorPrimary',
    label: (n) => `${n} ${plural(n, 'presupuesto esperando', 'presupuestos esperando')} aprobación`,
    to: `${paths.admin.budget.root}?stage=esperando_aprobar,en_espera`,
    cta: 'Revisar',
  },
  {
    etapa: 'APROBADO',
    icon: ToolOutlined,
    tintKey: 'colorSuccess',
    label: (n) =>
      `${n} ${plural(n, 'presupuesto aprobado', 'presupuestos aprobados')} para generar ODT`,
    to: `${paths.admin.budget.root}?stage=aprobado_sin_odt`,
    cta: 'Crear ODT',
  },
];

/**
 * "Tu ciclo hoy" — bandeja de acción del Home. Convierte el ciclo en una lista
 * de próximos pasos con su conteo y un deep-link al paso con el filtro aplicado.
 * Parte de la capa de auto-evidencia (Fase 4). Consume las columnas de
 * GET /kpi/ciclo.
 */
function BandejaCiclo({ columnas = [], loading = false }) {
  const { token } = useToken();

  const countByEtapa = React.useMemo(() => {
    const m = {};
    columnas.forEach((c) => {
      m[c.etapa] = c.count;
    });
    return m;
  }, [columnas]);

  // Si no hay nada accionable (y ya cargó), no mostrar la bandeja: evita ruido.
  const totalAccionable = ACCIONES.reduce((s, a) => s + (countByEtapa[a.etapa] || 0), 0);
  if (!loading && totalAccionable === 0) return null;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.07em',
            color: token.colorTextTertiary,
          }}
        >
          Tu ciclo hoy
        </div>
        <Link
          to={paths.admin.ciclo}
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: token.colorPrimary,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            whiteSpace: 'nowrap',
          }}
        >
          Ver tablero completo <RightOutlined style={{ fontSize: 10 }} />
        </Link>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {ACCIONES.map((a) => {
          const n = countByEtapa[a.etapa] || 0;
          const tint = token[a.tintKey];
          const Icon = a.icon;
          return (
            <Link key={a.etapa} to={a.to} style={{ textDecoration: 'none' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: token.paddingMD,
                  border: `1px solid ${token.colorBorder}`,
                  borderRadius: token.borderRadiusLG,
                  background: token.colorBgContainer,
                  boxShadow: token.boxShadowSecondary,
                }}
              >
                <IconBadge icon={Icon} tint={tint} size="md" />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 700,
                      color: token.colorText,
                      lineHeight: 1.1,
                    }}
                  >
                    {loading ? '—' : n}
                  </div>
                  <div
                    style={{ fontSize: 12.5, color: token.colorTextSecondary, lineHeight: 1.35 }}
                  >
                    {loading ? 'Cargando…' : a.label(n)}
                  </div>
                </div>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: tint,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {a.cta} <RightOutlined style={{ fontSize: 10 }} />
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default BandejaCiclo;
